import { useCallback, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ClientSearchField } from '@/entities/Client/ui/ClientSearchField';
import { ExecutorSearchField } from '@/entities/Executor/ui/ExecutorSearchField';
import { UserChatViewer } from '@/features/UserChatViewer/ui/UserChatViewer';
import { useUserChatProfile } from '@/entities/UserChat/model/profileStore';
import { getUserChatProfileName } from '@/entities/UserChat/mappers';
import type { ChatRole } from '@/entities/Chat/model/types';
import type { UserChatProfile } from '@/entities/UserChat/model/types';
import { useAuth } from '@/entities/Auth/store';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { RequestStatus } from '@/shared/model/requestStatus';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { CopyButton } from '@/shared/ui/CopyButton/CopyButton';
import { Link } from '@/shared/ui/Link/Link';
import { RequestError, RequestLoading, RequestEmpty } from '@/shared/ui/RequestState';
import { UserDirectory } from './UserDirectory';
import classes from './UserChatsPage.module.scss';

/** Открывает переписки клиентов и исполнителей в режиме просмотра */
export const UserChatsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const roleParam = searchParams.get('role');

    const role: ChatRole = roleParam === 'Executor'
        ? 'Executor'
        : 'Client';

    const roleValid = !roleParam || roleParam === 'Client' || roleParam === 'Executor';
    const profileId = searchParams.get('user_id');
    const selectedId = searchParams.get('dialogId') ?? searchParams.get('chat_id') ?? searchParams.get('chatId');
    const selection = useUserChatProfile((state) => state.selection);
    const profileStatus = useUserChatProfile((state) => state.status);
    const failure = useUserChatProfile((state) => state.error);
    const load = useUserChatProfile((state) => state.load);
    const select = useUserChatProfile((state) => state.select);
    const reset = useUserChatProfile((state) => state.reset);
    const sessionRevision = useAuth((state) => state.sessionRevision);
    const canRead = useCanAccessPermission('admin.chats.view');
    const canViewClients = useCanAccessPermission('admin.clients.view');
    const canViewExecutors = useCanAccessPermission('admin.executors.view');

    const canViewProfile = role === 'Client'
        ? canViewClients
        : canViewExecutors;

    const current = selection?.role === role && selection.profile.id === profileId
        ? selection
        : null;

    const shareUrl = new URL(`${location.pathname}${location.search}`, window.location.origin).href;

    useEffect(() => () => reset(), [sessionRevision, reset]);

    useEffect(() => {
        if (!profileId || !roleValid || !canRead || !canViewProfile) {
            reset();

            return;
        }

        const cached = useUserChatProfile.getState().selection;

        if (cached?.role === role && cached.profile.id === profileId) return;
        void load(profileId, role);
    }, [profileId, role, roleValid, canRead, canViewProfile, sessionRevision, load, reset]);

    /** Открывает переписки уже загруженного профиля без промежуточного экрана */
    const openOwner = useCallback((profile: UserChatProfile | null): void => {
        select(profile);

        setSearchParams(profile?.profile.id
            ? {
                role: profile.role,
                user_id: profile.profile.id
            }
            : { role });
    }, [select, setSearchParams, role]);

    /** Закрывает диалог, сохраняя выбранного пользователя и список переписок */
    const closeChat = (): void => {
        setSearchParams((previous) => {
            const next = new URLSearchParams(previous);
            next.delete('dialogId');
            next.delete('chat_id');
            next.delete('chatId');

            return next;
        });
    };

    if (!canRead || !canViewProfile) return <RequestEmpty message="Нет доступа к переписке пользователя" />;

    return <div className={classes.page}>
        <header className={classes.header}>
            <p>Выберите пользователя в списке или найдите по имени и телефону</p>
            <div className={classes.roleButtons}>
                <ActionButton
                    type="button"
                    label="Клиенты"
                    aria-pressed={role === 'Client'}
                    outlined={role !== 'Client'}
                    onClick={() => setSearchParams({ role: 'Client' })}
                />
                <ActionButton
                    type="button"
                    label="Исполнители"
                    aria-pressed={role === 'Executor'}
                    outlined={role !== 'Executor'}
                    onClick={() => setSearchParams({ role: 'Executor' })}
                />
                {selectedId &&
                    <ActionButton
                        type="button"
                        className={classes.closeDialog}
                        icon="pi pi-times"
                        iconOnly
                        text
                        size="small"
                        aria-label="Закрыть диалог"
                        title="Закрыть диалог"
                        color="secondary"
                        onClick={closeChat}
                    />
                }
            </div>
            <div className={classes.search}>
                {role === 'Client'
                    ? <ClientSearchField
                        name="chatClient"
                        label="Клиент"
                        value={current?.role === 'Client'
                            ? current.profile.id ?? null
                            : null
                        }
                        onChange={(_id, value) => openOwner(value
                            ? {
                                role: 'Client',
                                profile: value
                            }
                            : null)}
                    />
                    : <ExecutorSearchField
                        name="chatExecutor"
                        label="Исполнитель"
                        value={current?.role === 'Executor'
                            ? current.profile
                            : null
                        }
                        onChange={(value) => openOwner(value
                            ? {
                                role: 'Executor',
                                profile: value
                            }
                            : null)}
                    />}
            </div>
            {!roleValid &&
                <RequestError message="В ссылке указана неизвестная роль пользователя" />
            }
            {profileStatus === RequestStatus.Loading &&
                <RequestLoading message="Загружаем пользователя из ссылки..." />
            }
            {failure &&
                <RequestError
                    message={failure}
                    onRetry={profileId
                        ? () => load(profileId, role)
                        : undefined
                    }
                />
            }
            {current &&
                <div className={classes.ownerDetails}>
                    <p>Просмотр от лица: {getUserChatProfileName(current)}. Сообщения не отмечаются прочитанными.</p>
                    <Link
                        url={`/${role === 'Client'
                            ? 'clients'
                            : 'executors'}/${encodeURIComponent(profileId ?? '')}`}
                        text="Открыть профиль пользователя"
                        isBlank
                    />
                    <CopyButton
                        value={shareUrl}
                        label="Копировать ссылку на переписку"
                    />
                </div>
            }
            {current && !current.profile.appUserId &&
                <RequestEmpty message="У пользователя нет учётной записи приложения" />
            }
        </header>
        <div className={classes.workspace}>
            <UserDirectory
                key={sessionRevision}
                role={role}
                selectedProfileId={profileId}
                onSelect={openOwner}
            />
            <UserChatViewer ownerAppUserId={roleValid
                ? current?.profile.appUserId ?? null
                : null
            } />
        </div>
    </div>;
};
